import { useEffect, useState, useRef, useCallback } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/AuthContext";

// -------- Match pin helpers (localStorage-backed) --------
const PIN_KEYS = { a: "rm:match:pin_a", b: "rm:match:pin_b" };
const MATCH_PIN_EVENT = "rm:match:pin-change";

function readPin(slot) {
  try {
    const raw = window.localStorage.getItem(PIN_KEYS[slot]);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writePin(slot, riff) {
  try {
    if (riff) window.localStorage.setItem(PIN_KEYS[slot], JSON.stringify({ id: riff.id, title: riff.title, user_name: riff.user_name, user_email: riff.user_email }));
    else window.localStorage.removeItem(PIN_KEYS[slot]);
    window.dispatchEvent(new CustomEvent(MATCH_PIN_EVENT));
  } catch { /* noop */ }
}
export function usePinnedRiff(slot) {
  const [pin, setPin] = useState(() => readPin(slot));
  useEffect(() => {
    const sync = () => setPin(readPin(slot));
    window.addEventListener(MATCH_PIN_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(MATCH_PIN_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [slot]);
  return pin;
}

function PinToMatch({ riff, showToast }) {
  const navigate = useNavigate();
  const pinA = usePinnedRiff("a");
  const pinB = usePinnedRiff("b");
  const isA = pinA?.id === riff.id;
  const isB = pinB?.id === riff.id;

  const setSlot = (slot) => {
    writePin(slot, riff);
    // If the other slot has the same riff, clear it to avoid duplicate.
    const other = slot === "a" ? "b" : "a";
    if (readPin(other)?.id === riff.id) writePin(other, null);
    showToast?.(`Pinned to Match — slot ${slot.toUpperCase()}. Opening Match…`);
    setTimeout(() => navigate("/app/match"), 350);
  };
  const clear = (slot) => {
    writePin(slot, null);
    showToast?.(`Removed from Match slot ${slot.toUpperCase()}.`);
  };

  return (
    <div className="cf-pin-group" data-testid={`pin-group-${riff.id}`}>
      <button
        type="button"
        className={`cf-btn cf-btn--ghost cf-btn--sm cf-pin-btn ${isA ? "is-pinned" : ""}`}
        onClick={() => isA ? clear("a") : setSlot("a")}
        data-testid={`pin-a-${riff.id}`}
        title={isA ? "Unpin from Match slot A" : "Pin to Match slot A"}
      >
        {isA ? "📌 A ✓" : "📌 Match A"}
      </button>
      <button
        type="button"
        className={`cf-btn cf-btn--ghost cf-btn--sm cf-pin-btn ${isB ? "is-pinned" : ""}`}
        onClick={() => isB ? clear("b") : setSlot("b")}
        data-testid={`pin-b-${riff.id}`}
        title={isB ? "Unpin from Match slot B" : "Pin to Match slot B"}
      >
        {isB ? "📌 B ✓" : "📌 Match B"}
      </button>
    </div>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/unread-count`);
        if (alive) setUnread(data.unread_count || 0);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("notifs:refresh", tick);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("notifs:refresh", tick);
    };
  }, []);
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="cf-app">
      <header className="cf-app-top">
        <Link to="/app/capture" className="cf-brand" data-testid="app-brand">
          <span className="cf-brand__mark"><svg viewBox="0 0 32 32" width="24" height="24" fill="none"><rect x="1" y="1" width="30" height="30" rx="8" fill="#ef4444"/><path d="M10 22V10l6 4 6-4v12" stroke="#000" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
          <span className="cf-brand__name">RiffMaster</span>
        </Link>
        <div className="cf-app-top__right">
          <span className={`cf-plan-pill cf-plan-pill--${user?.subscription_status}`} data-testid="plan-badge">
            {user?.subscription_status === "paid" ? "PRO" : "FREE"}
          </span>
          <span className="cf-app-top__email" data-testid="app-user-email">{user?.email}</span>
          <button className="cf-btn cf-btn--ghost" onClick={doLogout} data-testid="logout-btn">Sign out</button>
        </div>
      </header>
      <main className="cf-app-main">
        <Outlet />
      </main>
      <nav className="cf-app-nav" data-testid="app-bottom-nav">
        {[
          ["capture", "Capture", "🎙"],
          ["library", "Library", "📁"],
          ["match", "Match", "♥"],
          ["global", "Global", "🌐"],
          ["inbox", "Inbox", "🔔"],
          ["profile", "Profile", "👤"],
          ...(user?.role === "admin" ? [["admin", "Admin", "⚙"]] : []),
        ].map(([key, label]) => (
          <NavLink key={key} to={`/app/${key}`} className={({isActive}) => "cf-app-nav__item" + (isActive ? " is-active" : "")} data-testid={`nav-${key}`}>
            <span>{label}{key === "inbox" && unread > 0 && (
              <span className="cf-nav-badge" data-testid="inbox-badge">{unread > 9 ? "9+" : unread}</span>
            )}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function CapturePage() {
  const { user, refresh } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("RIFF");
  const [bpm, setBpm] = useState("120");
  const [key, setKey] = useState("Em");
  const [genre, setGenre] = useState("rock");
  const [mood, setMood] = useState("dark");
  const [tags, setTags] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });

  // --- mic recording state ---
  const [mode, setMode] = useState("record"); // "record" | "upload"
  const [recState, setRecState] = useState("idle"); // idle | recording | done | denied | unsupported
  const [elapsed, setElapsed] = useState(0);
  const [recBlob, setRecBlob] = useState(null);
  const [recUrl, setRecUrl] = useState(null);
  const [levels, setLevels] = useState(new Array(32).fill(8));
  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);

  // --- tap tempo ---
  const [tapHint, setTapHint] = useState("");
  const tapsRef = useRef([]);
  const tapResetRef = useRef(null);
  const handleTapTempo = () => {
    const now = performance.now();
    const taps = tapsRef.current;
    // Reset if last tap was more than 2s ago
    if (taps.length && now - taps[taps.length - 1] > 2000) {
      tapsRef.current = [];
    }
    tapsRef.current.push(now);
    // Keep last 8 taps
    if (tapsRef.current.length > 8) tapsRef.current.shift();

    if (tapsRef.current.length >= 2) {
      const t = tapsRef.current;
      const intervals = [];
      for (let i = 1; i < t.length; i++) intervals.push(t[i] - t[i - 1]);
      const avgMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const computed = Math.round(60000 / avgMs);
      const clamped = Math.max(40, Math.min(240, computed));
      setBpm(String(clamped));
      setTapHint(`${tapsRef.current.length} tap${tapsRef.current.length === 1 ? "" : "s"} · ${clamped} BPM`);
    } else {
      setTapHint("Keep tapping…");
    }

    // Auto-reset hint after 2.5s of inactivity
    if (tapResetRef.current) clearTimeout(tapResetRef.current);
    tapResetRef.current = setTimeout(() => {
      tapsRef.current = [];
      setTapHint("");
    }, 2500);
  };

  useEffect(() => {
    return () => cleanupRecording();
    // eslint-disable-next-line
  }, []);

  const cleanupRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* noop */ } audioCtxRef.current = null; }
    analyserRef.current = null;
    mediaRecRef.current = null;
  };

  const startRecording = async () => {
    setMsg({kind:"",text:""});
    if (typeof window === "undefined" || !navigator.mediaDevices || !window.MediaRecorder) {
      setRecState("unsupported"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      // Pick the best mime type the browser supports
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mime = candidates.find(c => MediaRecorder.isTypeSupported(c)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setRecBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecUrl(url);
        setRecState("done");
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* noop */ } audioCtxRef.current = null; }
      };
      rec.start();
      setRecState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);

      // Set up live audio level for waveform animation
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        src.connect(analyser);
        analyserRef.current = analyser;
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!analyserRef.current) return;
          analyser.getByteFrequencyData(buf);
          const next = Array.from(buf).slice(0, 32).map(v => 8 + (v / 255) * 80);
          setLevels(next);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        // analyser is optional, no-op
      }
    } catch (err) {
      setRecState(err.name === "NotAllowedError" ? "denied" : "unsupported");
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
  };

  const discardRecording = () => {
    if (recUrl) URL.revokeObjectURL(recUrl);
    setRecBlob(null); setRecUrl(null); setElapsed(0); setRecState("idle");
    setLevels(new Array(32).fill(8));
  };

  const submit = async (e) => {
    e.preventDefault();
    let uploadFile = file;
    let filename = "riff.webm";
    if (mode === "record") {
      if (!recBlob) { setMsg({kind:"error",text:"Record something first."}); return; }
      const ext = recBlob.type.includes("mp4") ? "m4a" : recBlob.type.includes("ogg") ? "ogg" : "webm";
      filename = `riff-${Date.now()}.${ext}`;
      uploadFile = new File([recBlob], filename, { type: recBlob.type });
    } else if (!uploadFile) {
      setMsg({kind:"error",text:"Pick an audio file first."}); return;
    }
    if (!title.trim()) { setMsg({kind:"error",text:"Title is required."}); return; }

    setBusy(true); setMsg({kind:"",text:""});
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("title", title); fd.append("type", type);
    fd.append("bpm", bpm); fd.append("key", key); fd.append("genre", genre);
    fd.append("mood", mood); fd.append("tags", tags); fd.append("source", "human");
    fd.append("is_public", isPaid && isPublic ? "true" : "false");
    try {
      await axios.post(`${API}/uploads`, fd);
      setMsg({kind:"success",text:`"${title}" uploaded.`});
      setTitle(""); setTags("");
      if (mode === "record") discardRecording();
      else setFile(null);
      await refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsg({kind:"error", text: (typeof d === "string" ? d : "Upload failed.")});
    } finally { setBusy(false); }
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;
  const used = user?.monthly_upload_count ?? 0;
  const isPaid = user?.subscription_status === "paid";
  const cap = isPaid ? "∞" : 5;

  return (
    <section className="cf-page">
      <div className="cf-container">
        <p className="cf-eyebrow">QUICK CAPTURE</p>
        <h1 className="cf-display cf-display--md">Drop the riff.</h1>
        <p className="cf-lead cf-lead--muted" data-testid="quota-line">
          Uploads this month: <strong>{used}</strong> / {cap}
          {user?.subscription_status !== "paid" && <> · <Link className="cf-link" to="/app/profile">Upgrade for unlimited</Link></>}
        </p>

        <div className="cf-mode-toggle" data-testid="mode-toggle">
          <button type="button" className={`cf-mode ${mode==="record"?"is-active":""}`} onClick={()=>setMode("record")} data-testid="mode-record">🎙 Record</button>
          <button type="button" className={`cf-mode ${mode==="upload"?"is-active":""}`} onClick={()=>{setMode("upload"); discardRecording();}} data-testid="mode-upload">⬆ Upload file</button>
        </div>

        <form className="cf-form" onSubmit={submit} data-testid="capture-form">
          {mode === "record" ? (
            <div className="cf-rec-panel" data-testid="recorder">
              <div className={`cf-rec-wave ${recState==="recording"?"is-live":""}`} aria-hidden="true">
                {levels.map((h, i) => <span key={i} style={{height: `${h}%`}} />)}
              </div>
              <p className="cf-rec-time" data-testid="rec-time">{fmt(elapsed)}</p>
              {recState === "idle" && (
                <>
                  <p className="cf-rec-hint">Tap the button and start playing — we&apos;ll record straight from your mic.</p>
                  <button type="button" className="cf-rec-btn" onClick={startRecording} data-testid="rec-start" aria-label="Start recording">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/></svg>
                  </button>
                </>
              )}
              {recState === "recording" && (
                <>
                  <p className="cf-rec-hint cf-rec-hint--live">● Recording…</p>
                  <button type="button" className="cf-rec-btn cf-rec-btn--stop" onClick={stopRecording} data-testid="rec-stop" aria-label="Stop recording">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                  </button>
                </>
              )}
              {recState === "done" && recUrl && (
                <>
                  <p className="cf-rec-hint">Listen back. Looks good? Fill in the details below & upload.</p>
                  <audio controls src={recUrl} data-testid="rec-preview" />
                  <div className="cf-rec-actions">
                    <button type="button" className="cf-btn cf-btn--ghost" onClick={discardRecording} data-testid="rec-discard">Discard & retry</button>
                  </div>
                </>
              )}
              {recState === "denied" && (
                <p className="cf-form__msg is-error" data-testid="rec-denied">
                  Microphone access denied. Enable mic permission in your browser settings, then refresh — or switch to <button type="button" className="cf-link" onClick={()=>setMode("upload")}>Upload file</button>.
                </p>
              )}
              {recState === "unsupported" && (
                <p className="cf-form__msg is-error" data-testid="rec-unsupported">
                  Mic recording isn&apos;t supported in this browser. Try Chrome / Safari on desktop, or <button type="button" className="cf-link" onClick={()=>setMode("upload")}>upload a file</button> instead.
                </p>
              )}
            </div>
          ) : (
            <label className="cf-field cf-field--file">
              <span>Audio file (mp3, wav, m4a, ogg, flac · max 20 MB)</span>
              <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac,.aac,.webm" onChange={e=>setFile(e.target.files[0])} data-testid="capture-file"/>
              {file && <small>{file.name} · {Math.round(file.size/1024)} KB</small>}
            </label>
          )}

          <div className="cf-tabs">
            {["RIFF","LYRIC","MELODY","RHYTHM"].map(t => (
              <button type="button" key={t} className={`cf-tab ${type===t?"is-active":""}`} onClick={()=>setType(t)} data-testid={`type-${t.toLowerCase()}`}>{t}</button>
            ))}
          </div>
          <label className="cf-field"><span>Title</span><input type="text" value={title} onChange={e=>setTitle(e.target.value)} required placeholder="e.g. Midnight Groove" data-testid="capture-title"/></label>
          <div className="cf-row2">
            <label className="cf-field cf-field--bpm">
              <span>BPM {tapHint && <em className="cf-bpm-hint" data-testid="tap-hint">· {tapHint}</em>}</span>
              <div className="cf-bpm-row">
                <input type="number" value={bpm} onChange={e=>setBpm(e.target.value)} min="40" max="240" data-testid="capture-bpm"/>
                <button type="button" className="cf-btn cf-btn--ghost cf-btn--tap" onClick={handleTapTempo} data-testid="tap-tempo-btn" title="Tap to set BPM">Tap</button>
              </div>
            </label>
            <label className="cf-field"><span>Key</span><input type="text" value={key} onChange={e=>setKey(e.target.value)} placeholder="e.g. Em, C, F#" data-testid="capture-key"/></label>
          </div>
          <div className="cf-row2">
            <label className="cf-field"><span>Genre</span><input type="text" value={genre} onChange={e=>setGenre(e.target.value)} data-testid="capture-genre"/></label>
            <label className="cf-field"><span>Mood</span><input type="text" value={mood} onChange={e=>setMood(e.target.value)} data-testid="capture-mood"/></label>
          </div>
          <label className="cf-field"><span>Tags (comma-separated)</span><input type="text" value={tags} onChange={e=>setTags(e.target.value)} placeholder="groove, distorted, intro" data-testid="capture-tags"/></label>

          <div className={`cf-publish-toggle ${isPaid ? "" : "is-locked"}`} data-testid="publish-toggle">
            {isPaid ? (
              <label className="cf-publish-toggle__row">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={e => setIsPublic(e.target.checked)}
                  data-testid="capture-public"
                />
                <div>
                  <strong>Publish to Global Feed</strong>
                  <small>Other artists can discover and match with this riff.</small>
                </div>
              </label>
            ) : (
              <div className="cf-publish-toggle__row" data-testid="publish-locked">
                <span className="cf-lock-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                </span>
                <div>
                  <strong>Publish to Global Feed</strong>
                  <small>Sharing publicly is a Pro feature. Free uploads stay in your private Library.</small>
                </div>
                <Link
                  to="/app/profile"
                  className="cf-btn cf-btn--primary cf-btn--sm"
                  data-testid="publish-upgrade-btn"
                >
                  Subscribe — $9.99/mo
                </Link>
              </div>
            )}
          </div>

          {msg.text && <p className={`cf-form__msg is-${msg.kind}`} data-testid="capture-msg">{msg.text}</p>}
          <button
            className="cf-btn cf-btn--primary cf-btn--block"
            disabled={busy || (mode === "record" && recState !== "done") || (mode === "upload" && !file)}
            data-testid="capture-submit"
          >
            {busy ? "Uploading…" : mode === "record" ? "Upload recording" : "Upload riff"}
          </button>
        </form>
      </div>
    </section>
  );
}

export function LibraryPage() {
  const { user } = useAuth();
  const isPaid = user?.subscription_status === "paid";
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/uploads/me`, { params: { type: filter, q } });
    setRows(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateRow = (updated) => {
    setRows(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
  };

  return (
    <section className="cf-page">
      <div className="cf-container">
        <p className="cf-eyebrow">YOUR VAULT</p>
        <h1 className="cf-display cf-display--md">Personal Library</h1>
        <div className="cf-lib-bar">
          <input className="cf-search" placeholder="Search riffs, tags…" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()} data-testid="library-search"/>
          <div className="cf-chips">
            {["ALL","RIFF","LYRIC","MELODY","RHYTHM"].map(t => (
              <button key={t} className={`cf-chip ${filter===t?"is-active":""}`} onClick={()=>setFilter(t)} data-testid={`filter-${t.toLowerCase()}`}>{t}</button>
            ))}
          </div>
        </div>
        {loading ? <p className="cf-lead cf-lead--muted">Loading…</p> :
         rows.length === 0 ? (
          <div className="cf-empty">
            <h3>No ideas yet</h3>
            <p>Tap Capture to record your first riff.</p>
          </div>
         ) : (
          <ul className="cf-rifflist" data-testid="library-list">
            {rows.map(r => <RiffRow key={r.id} riff={r} isPaid={isPaid} onUpdate={updateRow} />)}
          </ul>
         )
        }
      </div>
    </section>
  );
}

function RiffRow({ riff, isPaid, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setVisibility = async (nextPublic) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/uploads/${riff.id}/visibility`, { is_public: nextPublic });
      onUpdate?.(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update visibility.");
    } finally {
      setBusy(false);
    }
  };

  const isPublic = !!riff.is_public;
  // Free users can always unpublish (make private), but cannot publish to Global.
  const canPublish = isPaid || isPublic;

  return (
    <li className="cf-rifflist__row" data-testid={`riff-${riff.id}`}>
      <div className="cf-rifflist__head">
        <span className="cf-pill cf-pill--human">● {riff.source?.toUpperCase() || "HUMAN"}</span>
        <strong>{riff.title}</strong>
        <span className="cf-meta-small">{riff.type}</span>
        <span
          className={`cf-vis-badge ${isPublic ? "is-public" : "is-private"}`}
          data-testid={`riff-visibility-${riff.id}`}
          title={isPublic ? "Visible on the Global feed" : "Only visible in your Library"}
        >
          {isPublic ? "🌐 Global" : "🔒 Local"}
        </span>
      </div>
      <audio controls src={`${API}/uploads/${riff.id}/file`} preload="none" />
      <p className="cf-meta">
        {riff.bpm && <>{riff.bpm} BPM · </>}{riff.key && <>{riff.key} · </>}{riff.genre && <>{riff.genre} · </>}{riff.mood}
        {riff.tags?.length > 0 && <> · {riff.tags.join(" · ")}</>}
      </p>
      <div className="cf-rifflist__actions">
        <PinToMatch riff={riff} />
        {isPublic ? (
          <button
            type="button"
            className="cf-btn cf-btn--ghost cf-btn--sm"
            onClick={() => setVisibility(false)}
            disabled={busy}
            data-testid={`unpublish-btn-${riff.id}`}
          >
            {busy ? "Saving…" : "Make Local"}
          </button>
        ) : canPublish ? (
          <button
            type="button"
            className="cf-btn cf-btn--primary cf-btn--sm"
            onClick={() => setVisibility(true)}
            disabled={busy}
            data-testid={`publish-btn-${riff.id}`}
          >
            {busy ? "Publishing…" : "Publish to Global"}
          </button>
        ) : (
          <Link
            to="/app/profile"
            className="cf-btn cf-btn--ghost cf-btn--sm"
            data-testid={`publish-locked-${riff.id}`}
            title="Publishing to the Global feed is a Pro feature"
          >
            🔒 Publish (Pro)
          </Link>
        )}
        {err && <span className="cf-form__msg is-error" data-testid={`visibility-err-${riff.id}`}>{err}</span>}
      </div>
    </li>
  );
}

export function MatchPage() {
  const { user } = useAuth();
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [last, setLast] = useState(null);
  const [mode, setMode] = useState("global");
  const pinA = usePinnedRiff("a");
  const pinB = usePinnedRiff("b");
  const audioARef = useRef(null);
  const audioBRef = useRef(null);

  // When A ends naturally, kick off B. Best-effort — autoplay may fail if
  // the browser blocked it, but since the user already pressed play on A,
  // most browsers treat the gesture as carried-over and allow B to start.
  const onAEnded = useCallback(() => {
    const b = audioBRef.current;
    if (!b) return;
    try { b.currentTime = 0; } catch { /* noop */ }
    const p = b.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, []);

  // Reset both players whenever the pair changes (swap, shuffle, mode, swipe).
  useEffect(() => {
    [audioARef.current, audioBRef.current].forEach(el => {
      if (!el) return;
      try { el.pause(); el.currentTime = 0; } catch { /* noop */ }
    });
  }, [pair?.a?.id, pair?.b?.id]);

  const loadReqId = useRef(0);

  const loadPair = useCallback(async (m = mode) => {
    const reqId = ++loadReqId.current;
    setLoading(true); setErr(""); setLast(null);
    try {
      const params = { mode: m };
      if (pinA?.id) params.pin_a_id = pinA.id;
      if (pinB?.id) params.pin_b_id = pinB.id;
      const { data } = await axios.get(`${API}/match/pair`, { params });
      if (reqId !== loadReqId.current) return; // stale response — discard
      setPair(data);
    } catch (e) {
      if (reqId !== loadReqId.current) return; // stale failure — discard
      setErr(e.response?.data?.detail || "Could not load pair.");
      setPair(null);
    } finally {
      if (reqId === loadReqId.current) setLoading(false);
    }
  }, [mode, pinA?.id, pinB?.id]);

  useEffect(() => { loadPair(mode); }, [mode, loadPair]);

  const clearPin = (slot) => { writePin(slot, null); };
  const clearAllPins = () => { writePin("a", null); writePin("b", null); };

  const swipe = async (action) => {
    if (!pair) return;
    try {
      const { data } = await axios.post(`${API}/match/swipe`, {
        riff_a_id: pair.a.id, riff_b_id: pair.b.id, action,
      });
      setLast({ ...data, pair_a: pair.a, pair_b: pair.b, saved: false, saving: false });
    } catch { /* noop */ }
    // Result now persists — user clicks "Next pair" when ready.
  };

  const savePair = async () => {
    if (!last || !last.pair_a || !last.pair_b || last.saving || last.saved) return;
    setLast(prev => prev ? { ...prev, saving: true } : prev);
    try {
      await axios.post(`${API}/match/save`, {
        riff_a_id: last.pair_a.id,
        riff_b_id: last.pair_b.id,
        ai_score: last.ai_score ?? null,
        ai_reasoning: last.ai_reasoning ?? "",
      });
      setLast(prev => prev ? { ...prev, saving: false, saved: true } : prev);
    } catch (e) {
      setLast(prev => prev ? { ...prev, saving: false, saveError: e.response?.data?.detail || "Could not save." } : prev);
    }
  };

  const nextPair = () => { setLast(null); loadPair(mode); };

  const MODES = [
    { key: "global",  label: "Global",  hint: "Two random riffs from the worldwide feed." },
    { key: "library", label: "Library", hint: "Shuffle two riffs from your own library — find unexpected pairings in your back catalogue." },
    { key: "mix",     label: "Mix",     hint: "One of yours + one from another artist — collab matchmaking." },
  ];
  const activeMode = MODES.find(m => m.key === mode);

  return (
    <section className="cf-page">
      <div className="cf-container">
        <p className="cf-eyebrow">RIFF MATCH</p>
        <h1 className="cf-display cf-display--md">Pair the magic.</h1>

        <div className="cf-match-modes" role="tablist" aria-label="Match source" data-testid="match-modes">
          {MODES.map(m => (
            <button
              key={m.key}
              role="tab"
              aria-selected={mode === m.key}
              className={`cf-match-mode ${mode === m.key ? "is-active" : ""}`}
              onClick={() => setMode(m.key)}
              data-testid={`match-mode-${m.key}`}
            >
              {m.label}
            </button>
          ))}
          <button
            type="button"
            className="cf-match-shuffle"
            onClick={() => loadPair(mode)}
            disabled={loading}
            aria-label="Shuffle — get a new random pair"
            title="Shuffle"
            data-testid="match-shuffle"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
            Shuffle
          </button>
        </div>
        <p className="cf-match-modes__hint" data-testid="match-mode-hint">{activeMode.hint}</p>

        {(pinA || pinB) && (
          <div className="cf-match-pins" data-testid="match-pins">
            <span className="cf-eyebrow">PINNED</span>
            <div className="cf-match-pins__slots">
              <div className="cf-match-pins__slot" data-testid="pin-slot-a">
                <span className="cf-match-pins__label">Slot A</span>
                {pinA ? (
                  <>
                    <strong className="cf-match-pins__title">{pinA.title}</strong>
                    <span className="cf-meta-small">@{(pinA.user_name || pinA.user_email || "?").split("@")[0]}</span>
                    <button
                      type="button"
                      className="cf-btn cf-btn--ghost cf-btn--sm"
                      onClick={() => clearPin("a")}
                      data-testid="pin-clear-a"
                    >Unpin A</button>
                  </>
                ) : (
                  <span className="cf-meta cf-match-pins__empty">Random from {mode === "library" ? "your library" : "Global"}</span>
                )}
              </div>
              <div className="cf-match-pins__slot" data-testid="pin-slot-b">
                <span className="cf-match-pins__label">Slot B</span>
                {pinB ? (
                  <>
                    <strong className="cf-match-pins__title">{pinB.title}</strong>
                    <span className="cf-meta-small">@{(pinB.user_name || pinB.user_email || "?").split("@")[0]}</span>
                    <button
                      type="button"
                      className="cf-btn cf-btn--ghost cf-btn--sm"
                      onClick={() => clearPin("b")}
                      data-testid="pin-clear-b"
                    >Unpin B</button>
                  </>
                ) : (
                  <span className="cf-meta cf-match-pins__empty">Random from {mode === "library" ? "your library" : "Global"}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="cf-link cf-match-pins__clear"
              onClick={clearAllPins}
              data-testid="pin-clear-all"
            >Clear all pins</button>
          </div>
        )}

        {loading && <p className="cf-lead cf-lead--muted">Loading pair…</p>}
        {err && <p className="cf-lead cf-lead--muted" data-testid="match-error">{err} <button className="cf-link" onClick={() => loadPair(mode)}>Try again</button></p>}
        {pair && !loading && (
          <div className="cf-match-card" data-testid="match-pair">
            <RiffSide riff={pair.a} position="a" audioRef={audioARef} onEnded={onAEnded}/>
            <button
              type="button"
              className="cf-match-card__swap"
              onClick={() => setPair({ a: pair.b, b: pair.a })}
              data-testid="match-swap"
              aria-label="Swap riff order"
              title="Swap A and B"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><path d="M7 7l5-5 5 5M12 2v20M17 17l-5 5-5-5"/></svg>
            </button>
            <RiffSide riff={pair.b} position="b" audioRef={audioBRef}/>
            <div className="cf-match-card__actions">
              <button className="cf-circle cf-circle--skip" onClick={()=>swipe("skip")} data-testid="match-skip" aria-label="Skip">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
              <button className="cf-circle cf-circle--like" onClick={()=>swipe("like")} data-testid="match-like" aria-label="Match">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-4.5-9-9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c-2 4.5-9 9-9 9z"/></svg>
              </button>
            </div>
          </div>
        )}
        {last && (
          <div className="cf-match-result" data-testid="match-result">
            {last.ai_score != null ? (
              <>
                <p className="cf-eyebrow">AI COMPATIBILITY</p>
                <h3 className="cf-display cf-display--sm" data-testid="match-result-score">{last.ai_score} / 100</h3>
                <p className="cf-lead" data-testid="match-result-reasoning">{last.ai_reasoning}</p>
                <p className="cf-meta cf-match-result__pair">
                  "<strong>{last.pair_a?.title}</strong>" + "<strong>{last.pair_b?.title}</strong>"
                </p>
                <div className="cf-match-result__actions">
                  <button
                    className={`cf-btn ${last.saved ? "cf-btn--ghost" : "cf-btn--primary"} cf-btn--sm`}
                    onClick={savePair}
                    disabled={last.saving || last.saved}
                    data-testid="match-save"
                  >
                    {last.saved ? "✓ Saved" : last.saving ? "Saving…" : "💾 Save pairing"}
                  </button>
                  <button
                    className="cf-btn cf-btn--ghost cf-btn--sm"
                    onClick={nextPair}
                    data-testid="match-next"
                  >Next pair →</button>
                </div>
                {last.saveError && <p className="cf-form__msg is-err">{last.saveError}</p>}
              </>
            ) : last.paid_only_message ? (
              <>
                <p className="cf-eyebrow">PRO FEATURE</p>
                <p className="cf-lead">{last.paid_only_message}</p>
                <div className="cf-match-result__actions">
                  <Link to="/app/profile" className="cf-btn cf-btn--primary" data-testid="upgrade-from-match">Upgrade to Pro</Link>
                  <button className="cf-btn cf-btn--ghost cf-btn--sm" onClick={nextPair} data-testid="match-next">Next pair →</button>
                </div>
              </>
            ) : null}
          </div>
        )}
        {user?.subscription_status !== "paid" && !last && (
          <p className="cf-meta" style={{marginTop:24}}>💡 Match with AI compatibility ratings is a Pro feature — <Link className="cf-link" to="/app/profile">upgrade for $9.99 AUD/month</Link>.</p>
        )}
      </div>
    </section>
  );
}

function RiffSide({ riff, position, audioRef, onEnded }) {
  return (
    <div className="cf-match-card__row" data-testid={position ? `match-side-${position}` : undefined}>
      <div className="cf-match-card__head">
        <span className="cf-pill cf-pill--human">● {(riff.source||"HUMAN").toUpperCase()}</span>
        {position && <span className="cf-pill cf-pill--slot" data-testid={`match-slot-${position}`}>{position.toUpperCase()}</span>}
        <span className="cf-handle">@{(riff.user_name || riff.user_email).split("@")[0]}</span>
      </div>
      <h4>{riff.title}</h4>
      <audio
        ref={audioRef}
        controls
        src={`${API}/uploads/${riff.id}/file`}
        preload="none"
        onEnded={onEnded}
        data-testid={position ? `match-audio-${position}` : undefined}
      />
      <p className="cf-meta">{riff.bpm && `${riff.bpm} BPM · `}{riff.key && `${riff.key} · `}{riff.genre} · {riff.mood}</p>
    </div>
  );
}

// ---------- Follow button (small, self-contained) ----------
export function FollowButton({ userId, isFollowing, onChange, size = "sm", className = "" }) {
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState(!!isFollowing);
  useEffect(() => { setFollowing(!!isFollowing); }, [isFollowing]);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (busy || !userId) return;
    setBusy(true);
    try {
      if (following) {
        const { data } = await axios.delete(`${API}/users/${userId}/follow`);
        setFollowing(false);
        onChange?.({ userId, is_following: false, followers_count: data.followers_count });
      } else {
        const { data } = await axios.post(`${API}/users/${userId}/follow`);
        setFollowing(true);
        onChange?.({ userId, is_following: true, followers_count: data.followers_count });
      }
    } catch { /* noop */ } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      className={`cf-btn ${following ? "cf-btn--ghost" : "cf-btn--primary"} ${size === "sm" ? "cf-btn--sm" : ""} cf-follow-btn ${className}`}
      onClick={toggle}
      disabled={busy}
      data-testid={`follow-btn-${userId}`}
      aria-pressed={following}
    >
      {busy ? "…" : following ? "Following" : "+ Follow"}
    </button>
  );
}

export function GlobalPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [collabFor, setCollabFor] = useState(null); // riff being requested
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    axios.get(`${API}/uploads/global`).then(({data}) => { setRows(data); setLoading(false); });
    axios.get(`${API}/follows/me`)
      .then(({data}) => setFollowingSet(new Set(data.following_ids || [])))
      .catch(() => setFollowingSet(new Set()));
  }, []);

  const onFollowChange = ({ userId, is_following }) => {
    setFollowingSet(prev => {
      const next = new Set(prev);
      if (is_following) next.add(userId); else next.delete(userId);
      return next;
    });
  };

  const openCollab = (riff) => { setCollabFor(riff); setMessage(""); };
  const closeCollab = () => { setCollabFor(null); setMessage(""); };

  const sendCollab = async () => {
    if (!collabFor) return;
    setSending(true);
    try {
      await axios.post(`${API}/collab/request`, {
        riff_id: collabFor.id,
        message: message.trim(),
      });
      setToast(`Request sent to @${(collabFor.user_name||collabFor.user_email).split("@")[0]} ✓`);
      closeCollab();
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setToast(e.response?.data?.detail || "Could not send request.");
      setTimeout(() => setToast(""), 4500);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="cf-page">
      <div className="cf-container">
        <p className="cf-eyebrow">GLOBAL FEED</p>
        <h1 className="cf-display cf-display--md">Find your scene.</h1>
        {toast && <p className="cf-form__msg is-ok" data-testid="collab-toast">{toast}</p>}
        {loading ? <p className="cf-lead cf-lead--muted">Loading feed…</p> :
         rows.length === 0 ? <div className="cf-empty"><h3>The feed is quiet</h3><p>Be the first to drop a riff.</p></div> :
         <ul className="cf-rifflist" data-testid="global-list">
           {rows.map(r => {
             const isOwn = user?.id === r.user_id;
             return (
               <li key={r.id} className="cf-rifflist__row">
                 <div className="cf-rifflist__head">
                   <span className="cf-pill cf-pill--human">● {(r.source||"HUMAN").toUpperCase()}</span>
                   <strong>{r.title}</strong>
                   <Link to={`/app/u/${r.user_id}`} className="cf-handle cf-handle--link" data-testid={`handle-link-${r.id}`}>
                     @{(r.user_name||r.user_email).split("@")[0]}
                   </Link>
                   {!isOwn && (
                     <FollowButton
                       userId={r.user_id}
                       isFollowing={followingSet.has(r.user_id)}
                       onChange={onFollowChange}
                     />
                   )}
                   <span className="cf-meta-small">{r.type}</span>
                 </div>
                 <audio controls src={`${API}/uploads/${r.id}/file`} preload="none" />
                 <p className="cf-meta">{r.bpm && `${r.bpm} BPM · `}{r.key && `${r.key} · `}{r.genre} · {r.mood}{r.tags?.length>0 && ` · ${r.tags.join(" · ")}`}</p>
                 <div className="cf-rifflist__actions">
                   <PinToMatch riff={r} showToast={(t) => { setToast(t); setTimeout(() => setToast(""), 3000); }} />
                   {!isOwn && (
                     <button
                       type="button"
                       className="cf-btn cf-btn--ghost cf-btn--sm"
                       onClick={() => openCollab(r)}
                       data-testid={`collab-btn-${r.id}`}
                       title="Request to collaborate"
                     >
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:6,verticalAlign:-2}}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                       Request to Collaborate
                     </button>
                   )}
                 </div>
               </li>
             );
           })}
         </ul>
        }

        {collabFor && (
          <div className="cf-modal-backdrop" onClick={closeCollab} data-testid="collab-modal">
            <div className="cf-modal" onClick={e=>e.stopPropagation()}>
              <p className="cf-eyebrow">COLLAB REQUEST</p>
              <h3 className="cf-display cf-display--sm">Reach out to @{(collabFor.user_name||collabFor.user_email).split("@")[0]}</h3>
              <p className="cf-lead cf-lead--muted">About "<strong>{collabFor.title}</strong>". Add a short note — they'll see it on their Profile and can Accept or Decline.</p>
              <textarea
                className="cf-textarea"
                placeholder="Hey — love the groove on this. Want to lay down a vocal line over it?"
                value={message}
                onChange={e=>setMessage(e.target.value)}
                maxLength={500}
                rows={4}
                data-testid="collab-message"
              />
              <p className="cf-meta" style={{textAlign:"right"}}>{message.length}/500</p>
              <div className="cf-modal__actions">
                <button className="cf-btn cf-btn--ghost" onClick={closeCollab} disabled={sending} data-testid="collab-cancel">Cancel</button>
                <button className="cf-btn cf-btn--primary" onClick={sendCollab} disabled={sending} data-testid="collab-send">
                  {sending ? "Sending…" : "Send request"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [teaserRiffs, setTeaserRiffs] = useState([]);
  const [collab, setCollab] = useState({ incoming: [], outgoing: [], pending_incoming: 0 });
  const [respondingId, setRespondingId] = useState(null);
  const [counts, setCounts] = useState({ followers: 0, following: 0 });
  const [showList, setShowList] = useState(null); // 'followers' | 'following'
  const [list, setList] = useState([]);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [savedPairs, setSavedPairs] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [deletingPairId, setDeletingPairId] = useState(null);
  const isFree = user?.subscription_status !== "paid";

  const loadCounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`${API}/users/${user.id}`);
      setCounts({ followers: data.followers_count, following: data.following_count });
    } catch { /* noop */ }
  }, [user?.id]);

  const loadCollab = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/collab/requests`);
      setCollab(data);
    } catch { /* noop */ }
  }, []);

  const loadSavedPairs = useCallback(async () => {
    setSavedLoading(true);
    try {
      const { data } = await axios.get(`${API}/match/saved`);
      setSavedPairs(data.pairs || []);
    } catch { setSavedPairs([]); } finally { setSavedLoading(false); }
  }, []);

  const deletePair = async (id) => {
    if (!window.confirm("Remove this paired riff?")) return;
    setDeletingPairId(id);
    try {
      await axios.delete(`${API}/match/saved/${id}`);
      setSavedPairs(prev => prev.filter(p => p.id !== id));
    } catch { /* noop */ } finally { setDeletingPairId(null); }
  };

  useEffect(() => {
    if (!isFree) return;
    axios.get(`${API}/uploads/global`)
      .then(({ data }) => setTeaserRiffs((data || []).slice(0, 2)))
      .catch(() => setTeaserRiffs([]));
  }, [isFree]);

  useEffect(() => { loadCollab(); }, [loadCollab]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadSavedPairs(); }, [loadSavedPairs]);
  useEffect(() => {
    axios.get(`${API}/follows/me`)
      .then(({data}) => setFollowingSet(new Set(data.following_ids || [])))
      .catch(() => {});
  }, []);

  const openList = async (which) => {
    if (!user?.id) return;
    setShowList(which);
    try {
      const { data } = await axios.get(`${API}/users/${user.id}/${which}`);
      setList(data.users || []);
    } catch { setList([]); }
  };

  const onFollowChange = ({ userId, is_following }) => {
    setFollowingSet(prev => {
      const next = new Set(prev);
      if (is_following) next.add(userId); else next.delete(userId);
      return next;
    });
    loadCounts();
  };

  const respond = async (reqId, action) => {
    setRespondingId(reqId);
    try {
      await axios.post(`${API}/collab/requests/${reqId}/respond`, { action });
      await loadCollab();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not respond.");
    } finally {
      setRespondingId(null);
    }
  };

  const upgrade = () => {
    const link = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
    if (!link) {
      alert("Upgrade link is not configured. Please contact support.");
      return;
    }
    const email = user?.email ? encodeURIComponent(user.email) : "";
    const url = email ? `${link}?prefilled_email=${email}` : link;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <section className="cf-page">
      <div className="cf-container">
        <p className="cf-eyebrow">PROFILE</p>
        <h1 className="cf-display cf-display--md">{user?.name || user?.email}</h1>

        <div className="cf-follow-counts" data-testid="follow-counts">
          <button className="cf-follow-counts__btn" onClick={() => openList("followers")} data-testid="my-followers">
            <strong>{counts.followers}</strong> follower{counts.followers === 1 ? "" : "s"}
          </button>
          <span className="cf-follow-counts__sep">·</span>
          <button className="cf-follow-counts__btn" onClick={() => openList("following")} data-testid="my-following">
            <strong>{counts.following}</strong> following
          </button>
          <span className="cf-follow-counts__sep">·</span>
          <span className="cf-follow-counts__btn cf-follow-counts__btn--static" data-testid="my-paired-count">
            <strong>{savedPairs.length}</strong> paired riff{savedPairs.length === 1 ? "" : "s"}
          </span>
        </div>

        <details className="cf-paired" data-testid="paired-section" open={savedPairs.length > 0 && savedPairs.length <= 3}>
          <summary className="cf-paired__summary">
            <span className="cf-eyebrow">PAIRED RIFFS</span>
            <span className="cf-paired__count">{savedPairs.length}</span>
          </summary>
          {savedLoading ? (
            <p className="cf-lead cf-lead--muted" style={{marginTop:12}}>Loading…</p>
          ) : savedPairs.length === 0 ? (
            <p className="cf-meta" style={{marginTop:12}} data-testid="paired-empty">
              No saved pairings yet. Head to <Link to="/app/match" className="cf-link">Match</Link> and tap "Save pairing" on any AI-rated pair.
            </p>
          ) : (
            <ul className="cf-paired__list" data-testid="paired-list">
              {savedPairs.map(p => (
                <li key={p.id} className="cf-paired__item" data-testid={`paired-row-${p.id}`}>
                  <div className="cf-paired__head">
                    {p.ai_score != null && (
                      <span className="cf-paired__score" data-testid={`paired-score-${p.id}`}>
                        <strong>{p.ai_score}</strong><small>/100</small>
                      </span>
                    )}
                    <div className="cf-paired__titles">
                      <span><strong>{p.a.title}</strong> <small>by @{(p.a.user_name||p.a.user_email).split("@")[0]}</small></span>
                      <span className="cf-paired__plus">＋</span>
                      <span><strong>{p.b.title}</strong> <small>by @{(p.b.user_name||p.b.user_email).split("@")[0]}</small></span>
                    </div>
                    <button
                      className="cf-btn cf-btn--ghost cf-btn--sm cf-paired__remove"
                      onClick={() => deletePair(p.id)}
                      disabled={deletingPairId === p.id}
                      data-testid={`paired-remove-${p.id}`}
                      aria-label="Remove pairing"
                      title="Remove"
                    >
                      {deletingPairId === p.id ? "…" : "✕"}
                    </button>
                  </div>
                  <div className="cf-paired__players">
                    <audio controls preload="none" src={`${API}/uploads/${p.a.id}/file`} />
                    <audio controls preload="none" src={`${API}/uploads/${p.b.id}/file`} />
                  </div>
                  {p.ai_reasoning && (
                    <p className="cf-paired__reasoning">{p.ai_reasoning}</p>
                  )}
                  <p className="cf-paired__meta">Saved {(p.created_at || "").slice(0, 10)}</p>
                </li>
              ))}
            </ul>
          )}
        </details>

        <div className="cf-profile-grid">
          <div className="cf-card">
            <h3>Account</h3>
            <p><strong>Email</strong> · {user?.email}</p>
            <p><strong>Joined</strong> · {user?.created_at?.slice(0,10)}</p>
            <p><strong>Plan</strong> · <span className={`cf-plan-pill cf-plan-pill--${user?.subscription_status}`}>{user?.subscription_status === "paid" ? "PRO" : "FREE"}</span></p>
            <p><strong>This month</strong> · {user?.monthly_upload_count || 0} uploads {user?.subscription_status === "paid" ? "(unlimited)" : `/ 5`}</p>
          </div>

          {user?.subscription_status === "paid" ? (
            <div className="cf-card cf-card--pro">
              <span className="cf-badge">YOU&apos;RE PRO</span>
              <h3>Pro Studio</h3>
              <p>$9.99 AUD / month · Unlimited uploads · Global feed · AI compatibility analysis on every match.</p>
              <div className="cf-manage-sub" data-testid="manage-sub-card">
                <h4>Manage subscription</h4>
                <p className="cf-meta">To cancel or update your payment method, email us at <a className="cf-link" href="mailto:billing@riffmaster.com.au?subject=Cancel%20RiffMaster%20Pro%20subscription">billing@riffmaster.com.au</a> from the email associated with your account. We&apos;ll process it within one business day.</p>
                <p className="cf-meta">Receipts and billing history are also available by replying to the original Stripe receipt email.</p>
              </div>
            </div>
          ) : (
            <div className="cf-card cf-card--pro" data-testid="upgrade-card">
              <span className="cf-badge">30-DAY FREE TRIAL</span>
              <h3>Go Pro — $9.99 AUD / month</h3>
              <ul>
                <li>Start with a 30-day free trial — cancel anytime</li>
                <li>Unlimited uploads (vs 5 / month on Free)</li>
                <li>AI compatibility analysis on every Match</li>
                <li>Full Global feed + Riff Match access</li>
              </ul>
              <button className="cf-btn cf-btn--primary cf-btn--block" onClick={upgrade} data-testid="upgrade-btn">Start 30-day free trial</button>
            </div>
          )}
        </div>

        {showList && (
          <div className="cf-modal-backdrop" onClick={() => setShowList(null)}>
            <div className="cf-modal" onClick={e=>e.stopPropagation()} data-testid={`my-list-modal-${showList}`}>
              <p className="cf-eyebrow">{showList.toUpperCase()}</p>
              <h3 className="cf-display cf-display--sm">Your {showList}</h3>
              {list.length === 0 ? (
                <p className="cf-lead cf-lead--muted" style={{marginTop:16}}>
                  {showList === "followers" ? "No followers yet — share your riffs!" : "You aren't following anyone yet."}
                </p>
              ) : (
                <ul className="cf-userlist">
                  {list.map(u => (
                    <li key={u.id} className="cf-userlist__row">
                      <Link to={`/app/u/${u.id}`} className="cf-userlist__main" onClick={() => setShowList(null)}>
                        <div className="cf-userlist__avatar" style={{background: u.color || "#ef4444"}}>{(u.name||"?").trim().charAt(0).toUpperCase()}</div>
                        <div>
                          <strong>{u.name}</strong>
                          <small>@{u.handle}</small>
                        </div>
                      </Link>
                      {u.id !== user?.id && (
                        <FollowButton
                          userId={u.id}
                          isFollowing={!!u.is_following || followingSet.has(u.id)}
                          onChange={onFollowChange}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="cf-modal__actions">
                <button className="cf-btn cf-btn--ghost" onClick={() => setShowList(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {(collab.incoming.length > 0 || collab.outgoing.length > 0) && (
          <div className="cf-collab" data-testid="collab-section">
            <div className="cf-collab__head">
              <p className="cf-eyebrow">COLLAB REQUESTS</p>
              <h3 className="cf-display cf-display--sm">
                {collab.pending_incoming > 0
                  ? <>You have <span className="cf-collab__count" data-testid="collab-pending-count">{collab.pending_incoming}</span> request{collab.pending_incoming === 1 ? "" : "s"} waiting</>
                  : "Your collaborations"}
              </h3>
            </div>

            {collab.incoming.length > 0 && (
              <>
                <p className="cf-collab__group-title">Incoming</p>
                <ul className="cf-collab__list" data-testid="collab-incoming">
                  {collab.incoming.map(r => (
                    <li key={r.id} className={`cf-collab__row cf-collab__row--${r.status}`} data-testid={`collab-row-${r.id}`}>
                      <div className="cf-collab__main">
                        <div className="cf-collab__line">
                          <strong>@{r.from_user_name?.split("@")[0]}</strong>
                          <span className="cf-handle">wants to collab on</span>
                          <strong>"{r.riff_title}"</strong>
                        </div>
                        {r.message && <p className="cf-collab__msg">"{r.message}"</p>}
                        {r.status === "accepted" && r.from_user_email && (
                          <p className="cf-collab__contact" data-testid={`collab-contact-${r.id}`}>
                            ✓ Reach out at <a href={`mailto:${r.from_user_email}`}>{r.from_user_email}</a>
                          </p>
                        )}
                      </div>
                      <div className="cf-collab__actions">
                        {r.status === "pending" ? (
                          <>
                            <button
                              className="cf-btn cf-btn--primary cf-btn--sm"
                              onClick={() => respond(r.id, "accept")}
                              disabled={respondingId === r.id}
                              data-testid={`collab-accept-${r.id}`}
                            >Accept</button>
                            <button
                              className="cf-btn cf-btn--ghost cf-btn--sm"
                              onClick={() => respond(r.id, "decline")}
                              disabled={respondingId === r.id}
                              data-testid={`collab-decline-${r.id}`}
                            >Decline</button>
                          </>
                        ) : (
                          <span className={`cf-collab__status cf-collab__status--${r.status}`}>{r.status}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {collab.outgoing.length > 0 && (
              <>
                <p className="cf-collab__group-title">Sent by you</p>
                <ul className="cf-collab__list" data-testid="collab-outgoing">
                  {collab.outgoing.map(r => (
                    <li key={r.id} className={`cf-collab__row cf-collab__row--${r.status}`} data-testid={`collab-row-${r.id}`}>
                      <div className="cf-collab__main">
                        <div className="cf-collab__line">
                          <span className="cf-handle">You requested collab on</span>
                          <strong>"{r.riff_title}"</strong>
                          <span className="cf-handle">from</span>
                          <strong>@{r.to_user_name?.split("@")[0]}</strong>
                        </div>
                        {r.status === "accepted" && r.to_user_email && (
                          <p className="cf-collab__contact" data-testid={`collab-contact-${r.id}`}>
                            ✓ They accepted! Reach out at <a href={`mailto:${r.to_user_email}`}>{r.to_user_email}</a>
                          </p>
                        )}
                      </div>
                      <span className={`cf-collab__status cf-collab__status--${r.status}`}>{r.status}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {isFree && teaserRiffs.length > 0 && (
          <div className="cf-teaser" data-testid="pro-teaser">
            <div className="cf-teaser__head">
              <p className="cf-eyebrow">DISCOVER</p>
              <h3 className="cf-display cf-display--sm">What Pros are sharing right now</h3>
              <p className="cf-lead cf-lead--muted">A peek inside the Global Feed — unlock to listen, match, and collaborate.</p>
            </div>
            <div className="cf-teaser__grid">
              {teaserRiffs.map((r, idx) => (
                <article key={r.id} className="cf-teaser__card" data-testid={`teaser-card-${idx}`} aria-hidden="true">
                  <div className="cf-teaser__blur">
                    <div className="cf-teaser__row-head">
                      <span className="cf-pill cf-pill--human">● {(r.source||"HUMAN").toUpperCase()}</span>
                      <strong>{r.title}</strong>
                      <span className="cf-handle">@{(r.user_name||r.user_email).split("@")[0]}</span>
                    </div>
                    <div className="cf-teaser__waveform" aria-hidden="true">
                      {Array.from({length: 28}).map((_, i) => (
                        <span key={i} style={{height: `${20 + Math.sin(i * 0.7 + idx) * 30 + 30}%`}} />
                      ))}
                    </div>
                    <p className="cf-meta">{r.bpm && `${r.bpm} BPM · `}{r.key && `${r.key} · `}{r.genre} · {r.mood}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="cf-teaser__cta">
              <span className="cf-lock-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
              </span>
              <div>
                <strong>Subscribe to unlock the Global Feed</strong>
                <small>$9.99 AUD/month · 30-day free trial · Unlimited uploads + AI match analysis · Cancel anytime.</small>
              </div>
              <button className="cf-btn cf-btn--primary" onClick={upgrade} data-testid="teaser-upgrade-btn">
                Start free trial
              </button>
            </div>
          </div>
        )}

        <p className="cf-meta" style={{marginTop:32}}>Need to refresh? <button className="cf-link" onClick={refresh}>Reload account</button></p>
      </div>
    </section>
  );
}

export function BillingReturnPage() {
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", message: "Checking your payment…" });
  const nav = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState({status:"error", message:"Missing session id."}); return; }

    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({status:"success", message:"Payment successful — you're now Pro. Redirecting…"});
          setTimeout(()=>nav("/app/profile"), 1800);
          return;
        }
        if (data.status === "expired") { setState({status:"error", message:"Checkout expired. Please try again."}); return; }
        if (attempts < 6) setTimeout(poll, 2000);
        else setState({status:"pending", message:"Still processing. Refresh in a minute to confirm."});
      } catch (e) {
        setState({status:"error", message: e.response?.data?.detail || "Could not verify payment."});
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <section className="cf-page" style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"60vh"}}>
      <div className="cf-container cf-container--narrow" style={{textAlign:"center"}} data-testid="billing-return">
        <p className="cf-eyebrow">{state.status === "success" ? "WELCOME TO PRO" : "PAYMENT"}</p>
        <h1 className="cf-display cf-display--md">{state.message}</h1>
        {state.status === "error" && <Link to="/app/profile" className="cf-btn cf-btn--primary" style={{marginTop:24}}>Back to profile</Link>}
      </div>
    </section>
  );
}

export function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("riffs"); // 'riffs' | 'users'
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [paidTotal, setPaidTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState({ kind: "", text: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setMsg({ kind: "", text: "" });
    try {
      const endpoint = tab === "users" ? "/admin/users" : "/admin/riffs";
      const { data } = await axios.get(`${API}${endpoint}`, {
        params: q ? { q } : {},
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setPaidTotal(data.paid_total || 0);
    } catch (e) {
      setMsg({ kind: "err", text: e.response?.data?.detail || `Failed to load ${tab}.` });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, tab]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
  }, [load, user?.role]);

  const onDelete = async (riff) => {
    const ok = window.confirm(
      `Delete "${riff.title}" by @${(riff.user_name || riff.user_email || "").split("@")[0]}?\n\nThis hard-deletes the audio file from storage and removes the database record. This cannot be undone.`
    );
    if (!ok) return;
    setBusyId(riff.id);
    setMsg({ kind: "", text: "" });
    try {
      const { data } = await axios.delete(`${API}/admin/riffs/${riff.id}`);
      setRows((prev) => prev.filter((r) => r.id !== riff.id));
      setTotal((t) => Math.max(0, t - 1));
      setMsg({
        kind: "ok",
        text: `Deleted "${data.title}" · storage:${data.storage_deleted ? "✓" : "✗"} · local:${data.local_deleted ? "✓" : "✗"}`,
      });
    } catch (e) {
      setMsg({ kind: "err", text: e.response?.data?.detail || "Delete failed." });
    } finally {
      setBusyId(null);
    }
  };

  if (user?.role !== "admin") {
    return (
      <section className="cf-page">
        <div className="cf-container">
          <p className="cf-eyebrow">ADMIN</p>
          <h1 className="cf-display cf-display--md">Not authorised</h1>
          <p className="cf-lead cf-lead--muted">This page is admin-only.</p>
          <Link to="/app/profile" className="cf-btn cf-btn--ghost" style={{ marginTop: 16 }}>Back to profile</Link>
        </div>
      </section>
    );
  }

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <section className="cf-page" data-testid="admin-page">
      <div className="cf-container">
        <p className="cf-eyebrow">ADMIN · MODERATION</p>
        <h1 className="cf-display cf-display--md">{tab === "users" ? "All users" : "All riffs"}</h1>
        <p className="cf-lead cf-lead--muted">
          {tab === "users"
            ? <>Showing {rows.length} of {total} total · {paidTotal} on Pro plan.</>
            : <>Showing {rows.length} of {total} total. Hard-delete removes both the DB record and the audio file from object storage.</>}
        </p>

        <div className="cf-admin-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "riffs"}
            className={`cf-admin-tab ${tab === "riffs" ? "is-active" : ""}`}
            onClick={() => { setTab("riffs"); setQ(""); setRows([]); }}
            data-testid="admin-tab-riffs"
          >Riffs</button>
          <button
            role="tab"
            aria-selected={tab === "users"}
            className={`cf-admin-tab ${tab === "users" ? "is-active" : ""}`}
            onClick={() => { setTab("users"); setQ(""); setRows([]); }}
            data-testid="admin-tab-users"
          >Users</button>
        </div>

        <div className="cf-admin-controls">
          <input
            type="search"
            placeholder={tab === "users" ? "Search by email or name…" : "Search by title…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="cf-admin-search"
            data-testid="admin-search"
          />
          <button className="cf-btn cf-btn--ghost" onClick={load} data-testid="admin-refresh">
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {msg.text && (
          <p className={`cf-form__msg is-${msg.kind}`} data-testid="admin-msg">{msg.text}</p>
        )}

        {tab === "users" ? (
          <div className="cf-admin-tablewrap">
            <table className="cf-admin-table" data-testid="admin-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Role</th>
                  <th>Riffs</th>
                  <th>Followers</th>
                  <th>Quota</th>
                  <th>Joined</th>
                  <th aria-label="Profile"></th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan="9" className="cf-admin-empty">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="9" className="cf-admin-empty">No users found.</td></tr>
                ) : rows.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td><strong>{u.name || "—"}</strong></td>
                    <td className="cf-mono">{u.email}</td>
                    <td>
                      <span className={`cf-pill ${u.subscription_status === "paid" ? "cf-pill--pro" : "cf-pill--free"}`}>
                        {u.subscription_status === "paid" ? "PRO" : "FREE"}
                      </span>
                    </td>
                    <td>{u.role === "admin" ? "⚙ admin" : "user"}</td>
                    <td>{u.riff_count} <span className="cf-meta-small">({u.public_riff_count} public)</span></td>
                    <td>{u.follower_count}</td>
                    <td className="cf-mono">{u.monthly_upload_count}{u.subscription_status === "paid" ? "" : "/5"}</td>
                    <td className="cf-mono">{u.created_at?.slice(0, 10)}</td>
                    <td>
                      <Link
                        to={`/app/u/${u.id}`}
                        className="cf-btn cf-btn--ghost cf-btn--sm"
                        data-testid={`admin-user-view-${u.id}`}
                      >View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cf-admin-tablewrap">
            <table className="cf-admin-table" data-testid="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Owner</th>
                  <th>Type</th>
                  <th>Meta</th>
                  <th>Public</th>
                  <th>Plays</th>
                  <th>Size</th>
                  <th>Created</th>
                  <th>Preview</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan="10" className="cf-admin-empty">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="10" className="cf-admin-empty">No riffs found.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`}>
                    <td><strong>{r.title}</strong></td>
                    <td className="cf-mono">{r.user_email}</td>
                    <td>{r.type}</td>
                    <td className="cf-meta">{r.bpm ? `${r.bpm} BPM · ` : ""}{r.key ? `${r.key} · ` : ""}{r.genre || "—"}</td>
                    <td>{r.is_public ? "🌐" : "🔒"}</td>
                    <td>{r.plays}</td>
                    <td className="cf-mono">{fmtBytes(r.file_size)}</td>
                    <td className="cf-mono">{r.created_at?.slice(0, 10)}</td>
                    <td><audio controls preload="none" src={`${API}/uploads/${r.id}/file`} style={{ height: 28, width: 180 }} /></td>
                    <td>
                      <button
                        className="cf-btn cf-btn--danger cf-btn--sm"
                        onClick={() => onDelete(r)}
                        disabled={busyId === r.id}
                        data-testid={`admin-delete-${r.id}`}
                        title="Hard delete"
                      >
                        {busyId === r.id ? "…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}



// ============================================================ PUBLIC PROFILE
export function PublicProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showList, setShowList] = useState(null); // 'followers' | 'following' | null
  const [list, setList] = useState([]);
  const [followingSet, setFollowingSet] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/users/${userId}`);
      setProfile(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load profile.");
      setProfile(null);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/follows/me`)
      .then(({data}) => setFollowingSet(new Set(data.following_ids || [])))
      .catch(() => {});
  }, []);

  const openList = async (which) => {
    setShowList(which);
    try {
      const { data } = await axios.get(`${API}/users/${userId}/${which}`);
      setList(data.users || []);
    } catch { setList([]); }
  };

  const onFollowChange = ({ userId: uid, is_following, followers_count }) => {
    setFollowingSet(prev => {
      const next = new Set(prev);
      if (is_following) next.add(uid); else next.delete(uid);
      return next;
    });
    if (uid === userId && typeof followers_count === "number") {
      setProfile(p => p ? { ...p, followers_count, is_following } : p);
    }
  };

  if (loading) return <section className="cf-page"><div className="cf-container"><p className="cf-lead cf-lead--muted">Loading profile…</p></div></section>;
  if (err) return <section className="cf-page"><div className="cf-container"><h1 className="cf-display cf-display--md">Not found</h1><p className="cf-lead cf-lead--muted">{err}</p><Link to="/app/global" className="cf-btn cf-btn--ghost">Back to feed</Link></div></section>;
  if (!profile) return null;

  return (
    <section className="cf-page" data-testid="public-profile">
      <div className="cf-container">
        <p className="cf-eyebrow">ARTIST</p>
        <div className="cf-pubprofile__head">
          <div
            className="cf-pubprofile__avatar"
            style={{ background: profile.color || "#ef4444" }}
            aria-hidden="true"
          >
            {(profile.name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div className="cf-pubprofile__meta">
            <h1 className="cf-display cf-display--md" data-testid="public-profile-name">{profile.name}</h1>
            <p className="cf-handle">@{profile.handle}{profile.subscription_status === "paid" && <span className="cf-pill cf-pill--pro" style={{marginLeft:8}}>PRO</span>}</p>
            <p className="cf-meta">
              {profile.instrument && <>🎚 {profile.instrument} · </>}
              {profile.location && <>📍 {profile.location} · </>}
              Joined {(profile.joined_at || "").slice(0, 10)}
            </p>
          </div>
          {!profile.is_self && (
            <FollowButton
              userId={profile.id}
              isFollowing={!!profile.is_following}
              onChange={onFollowChange}
              size="lg"
              className="cf-pubprofile__followbtn"
            />
          )}
        </div>

        <div className="cf-pubprofile__stats">
          <button className="cf-pubprofile__stat" onClick={() => openList("followers")} data-testid="public-profile-followers">
            <strong>{profile.followers_count}</strong> follower{profile.followers_count === 1 ? "" : "s"}
          </button>
          <button className="cf-pubprofile__stat" onClick={() => openList("following")} data-testid="public-profile-following">
            <strong>{profile.following_count}</strong> following
          </button>
          <div className="cf-pubprofile__stat cf-pubprofile__stat--static">
            <strong>{profile.riffs_count}</strong> public riff{profile.riffs_count === 1 ? "" : "s"}
          </div>
        </div>

        <h3 className="cf-display cf-display--sm" style={{marginTop:24}}>Public riffs</h3>
        {profile.riffs.length === 0 ? (
          <p className="cf-lead cf-lead--muted">No public riffs yet.</p>
        ) : (
          <ul className="cf-rifflist" data-testid="public-profile-riffs">
            {profile.riffs.map(r => (
              <li key={r.id} className="cf-rifflist__row">
                <div className="cf-rifflist__head">
                  <span className="cf-pill cf-pill--human">● {(r.source||"HUMAN").toUpperCase()}</span>
                  <strong>{r.title}</strong>
                  <span className="cf-meta-small">{r.type}</span>
                </div>
                <audio controls src={`${API}/uploads/${r.id}/file`} preload="none" />
                <p className="cf-meta">{r.bpm && `${r.bpm} BPM · `}{r.key && `${r.key} · `}{r.genre} · {r.mood}{r.tags?.length>0 && ` · ${r.tags.join(" · ")}`}</p>
              </li>
            ))}
          </ul>
        )}

        {showList && (
          <div className="cf-modal-backdrop" onClick={() => setShowList(null)}>
            <div className="cf-modal" onClick={e=>e.stopPropagation()} data-testid={`list-modal-${showList}`}>
              <p className="cf-eyebrow">{showList.toUpperCase()}</p>
              <h3 className="cf-display cf-display--sm">{profile.name}'s {showList}</h3>
              {list.length === 0 ? (
                <p className="cf-lead cf-lead--muted" style={{marginTop:16}}>Nobody here yet.</p>
              ) : (
                <ul className="cf-userlist">
                  {list.map(u => (
                    <li key={u.id} className="cf-userlist__row">
                      <Link to={`/app/u/${u.id}`} className="cf-userlist__main" onClick={() => setShowList(null)}>
                        <div className="cf-userlist__avatar" style={{background: u.color || "#ef4444"}}>{(u.name||"?").trim().charAt(0).toUpperCase()}</div>
                        <div>
                          <strong>{u.name}</strong>
                          <small>@{u.handle}</small>
                        </div>
                      </Link>
                      {u.id !== user?.id && (
                        <FollowButton
                          userId={u.id}
                          isFollowing={!!u.is_following || followingSet.has(u.id)}
                          onChange={onFollowChange}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="cf-modal__actions">
                <button className="cf-btn cf-btn--ghost" onClick={() => setShowList(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


// ============================================================ INBOX (notifications)
function _timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return iso.slice(0, 10);
}

export function InboxPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyAll, setBusyAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setRows(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    setBusyAll(true);
    try {
      await axios.post(`${API}/notifications/mark-read`, {});
      setRows(prev => prev.map(r => ({ ...r, read: true })));
      setUnread(0);
      window.dispatchEvent(new Event("notifs:refresh"));
    } catch { /* noop */ } finally { setBusyAll(false); }
  };

  const onOpen = async (n) => {
    // optimistically mark single notif as read, then navigate
    if (!n.read) {
      try {
        await axios.post(`${API}/notifications/mark-read`, { ids: [n.id] });
        setRows(prev => prev.map(r => r.id === n.id ? { ...r, read: true } : r));
        setUnread(c => Math.max(0, c - 1));
        window.dispatchEvent(new Event("notifs:refresh"));
      } catch { /* noop */ }
    }
    if (n.type === "new_riff" && n.from_user_id) {
      navigate(`/app/u/${n.from_user_id}`);
    }
  };

  return (
    <section className="cf-page" data-testid="inbox-page">
      <div className="cf-container">
        <p className="cf-eyebrow">INBOX</p>
        <h1 className="cf-display cf-display--md">Notifications</h1>
        <div className="cf-inbox-controls">
          <p className="cf-meta" data-testid="inbox-unread">
            {unread === 0 ? "All caught up." : `${unread} unread`}
          </p>
          {unread > 0 && (
            <button
              className="cf-btn cf-btn--ghost cf-btn--sm"
              onClick={markAllRead}
              disabled={busyAll}
              data-testid="inbox-mark-all"
            >
              {busyAll ? "Marking…" : "Mark all read"}
            </button>
          )}
        </div>

        {loading && <p className="cf-lead cf-lead--muted">Loading…</p>}
        {!loading && rows.length === 0 && (
          <div className="cf-empty" data-testid="inbox-empty">
            <h3>Nothing here yet</h3>
            <p>Follow some artists to get pinged when they drop new riffs.</p>
            <Link to="/app/global" className="cf-btn cf-btn--ghost" style={{marginTop: 12}}>Find artists</Link>
          </div>
        )}

        {rows.length > 0 && (
          <ul className="cf-notif-list" data-testid="inbox-list">
            {rows.map(n => (
              <li
                key={n.id}
                className={`cf-notif ${n.read ? "" : "cf-notif--unread"}`}
                onClick={() => onOpen(n)}
                data-testid={`notif-${n.id}`}
              >
                <span className="cf-notif__icon" aria-hidden="true">🎵</span>
                <div className="cf-notif__body">
                  <p className="cf-notif__line">
                    <strong>@{(n.from_user_name || "?").split("@")[0]}</strong> dropped a new riff: <strong>"{n.riff_title}"</strong>
                  </p>
                  <p className="cf-notif__meta">{_timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="cf-notif__dot" aria-label="unread" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
